import { useRef } from 'react';
import {
  Alert, Button, Icon, TextArea, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import { isAuthentication, isPasskey } from '../state';
import { friendlyError } from './errorMessages';
import EmojiPickerButton from './EmojiPickerButton';
import { BodySampleFields } from './Variables';

// DTO-exact (Form/validate.js's own BODY_MAX_CHARS).
const BODY_MAX_CHARS = 1024;

// the console's own toolbar icons (gs-ui-templates/src/components/WhatsappEditor/config.ts) — reuse
// them instead of our library's generic glyphs so the toolbar matches Gupshup's UI. The emoji
// button's own icon now lives in EmojiPickerButton.js alongside the picker it triggers.
const TOOLBAR_ICON_PREFIX = 'https://gs-upload.gupshup.io/templates/icons/ic_';

const TOOLBAR_ACTIONS = [
  {
    key: 'bold', kind: 'wrap', marker: '*', icon: `${TOOLBAR_ICON_PREFIX}bold.svg`, alt: 'Bold',
  },
  {
    key: 'italic', kind: 'wrap', marker: '_', icon: `${TOOLBAR_ICON_PREFIX}italics.svg`, alt: 'Italic',
  },
  {
    key: 'strike', kind: 'wrap', marker: '~', icon: `${TOOLBAR_ICON_PREFIX}strikethrough.svg`, alt: 'Strikethrough',
  },
  {
    key: 'mono', kind: 'wrap', marker: '```', icon: `${TOOLBAR_ICON_PREFIX}monospace.svg`, alt: 'Monospace',
  },
  {
    key: 'numbered', kind: 'line', prefixKind: 'numbered', icon: `${TOOLBAR_ICON_PREFIX}numbered_list.svg`, alt: 'Numbered list',
  },
  {
    key: 'bullet', kind: 'line', prefixKind: 'bullet', icon: `${TOOLBAR_ICON_PREFIX}bullet_list.svg`, alt: 'Bulleted list',
  },
];

function wrapSelection(textareaEl, value, marker) {
  const start = textareaEl ? (textareaEl.selectionStart ?? value.length) : value.length;
  const end = textareaEl ? (textareaEl.selectionEnd ?? value.length) : value.length;
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}${marker}${selected}${marker}${value.slice(end)}`;
  return { next, caretStart: start + marker.length, caretEnd: start + marker.length + selected.length };
}

// Numbered/bulleted list toggle — a line prefix, not a selection wrapper, so wrapSelection
// doesn't apply: it has to find the affected lines' own boundaries and rewrite each of them.
function toggleLinePrefix(textareaEl, value, kind) {
  const start = textareaEl ? (textareaEl.selectionStart ?? value.length) : value.length;
  const rawEnd = textareaEl ? (textareaEl.selectionEnd ?? value.length) : value.length;
  // A selection that ends exactly at the start of a line (right after that line's own newline)
  // hasn't actually touched that next line — pull end back so it isn't swept in as "affected".
  const end = rawEnd > start && value[rawEnd - 1] === '\n' ? rawEnd - 1 : rawEnd;

  // lastIndexOf('\n', start - 1) already answers "start of the line containing start" for every
  // start > 0; start === 0 is special-cased because lastIndexOf clamps a negative fromIndex back
  // to 0, which would wrongly match a newline that sits AT index 0 rather than before it.
  const lineStart = start === 0 ? 0 : value.lastIndexOf('\n', start - 1) + 1;
  const nextNewline = value.indexOf('\n', end);
  const lineEnd = nextNewline === -1 ? value.length : nextNewline;

  const before = value.slice(0, lineStart);
  const after = value.slice(lineEnd);
  const lines = value.slice(lineStart, lineEnd).split('\n');

  const prefixPattern = kind === 'bullet' ? /^\* / : /^\d{1,2}\. /;
  const alreadyPrefixed = lines.every((line) => prefixPattern.test(line));

  const nextLines = lines.map((line, index) => {
    if (alreadyPrefixed) {
      return line.replace(prefixPattern, '');
    }
    return kind === 'bullet' ? `* ${line}` : `${index + 1}. ${line}`;
  });

  const nextBlock = nextLines.join('\n');
  const next = `${before}${nextBlock}${after}`;
  return { next, caretStart: before.length, caretEnd: before.length + nextBlock.length };
}

function nextVariableToken(text) {
  const numbers = (text.match(/\{\{(\d+)\}\}/g) || []).map((token) => Number(token.slice(2, -2)));
  const max = numbers.length ? Math.max(...numbers) : 0;
  return `{{${max + 1}}}`;
}

// 5. Body — plain TextArea with a formatting toolbar (bold/italic/strike/mono/numbered/bullet)
// and an "Add variable" button below it, matching the console's layout.
//
// Deliberate divergence from the console: gs-ui-templates' Body/index.tsx uses a draft-js-backed
// WhatsappEditor with live marker fading and token renumbering. That editor and its dependency
// chain are not available here (plain JS, no draft-js per this repo's constraints), so this is a
// plain controlled textarea: the toolbar buttons wrap the current selection, or prefix the
// affected lines, with WhatsApp's own markdown characters via setSelectionRange, same visual
// result once rendered, less machinery. The live preview still renders the formatting correctly
// either way.
//
// Emoji is the exception to the textarea-trick pattern above: it's backed by the same
// emoji-picker-react package the console uses (now via the shared EmojiPickerButton, which owns
// the picker's own open/close state), inserted at the caret the same way handleAddVariable
// inserts a variable token below.
const BodySection = ({ state, dispatch, errors, disabled }) => {
  const textareaRef = useRef(null);

  if (isAuthentication(state) && !isPasskey(state)) {
    // Authentication templates (other than Passkey) send a fixed, server-supplied body — the
    // auth-extras task owns populating/locking it. Nothing to render here for that case.
    return null;
  }

  const { value, variable } = state.body;
  const hasBodyVariables = variable.length > 0;

  const applyNextValue = (next, caretStart, caretEnd) => {
    dispatch({ type: 'merge', path: 'body', value: { value: next, error: '' } });
    const el = textareaRef.current;
    if (el && caretStart !== undefined) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caretStart, caretEnd ?? caretStart);
      });
    }
  };

  const handleChange = (event) => {
    applyNextValue(event.target.value);
  };

  const handleToolbarClick = (action) => {
    const { next, caretStart, caretEnd } = action.kind === 'line'
      ? toggleLinePrefix(textareaRef.current, value, action.prefixKind)
      : wrapSelection(textareaRef.current, value, action.marker);
    applyNextValue(next, caretStart, caretEnd);
  };

  const handleAddVariable = () => {
    const el = textareaRef.current;
    const pos = el ? (el.selectionStart ?? value.length) : value.length;
    const token = nextVariableToken(value);
    // maxLength on the textarea only blocks direct typing, not a programmatic splice — guard here too.
    if (value.length + token.length > BODY_MAX_CHARS) return;
    const next = `${value.slice(0, pos)}${token}${value.slice(pos)}`;
    applyNextValue(next, pos + token.length);
  };

  // Same caret-splice as handleAddVariable above, but the advance is emoji.length rather than a
  // fixed token length — an emoji is 2+ UTF-16 code units (surrogate pairs, ZWJ sequences), so
  // advancing by 1 would leave the caret inside it. EmojiPickerButton hands back the plain emoji
  // string and closes its own popover; this only ever has to apply the insertion.
  const handleEmojiSelect = (emoji) => {
    const el = textareaRef.current;
    const pos = el ? (el.selectionStart ?? value.length) : value.length;
    // Same overflow guard as handleAddVariable above.
    if (value.length + emoji.length > BODY_MAX_CHARS) return;
    const next = `${value.slice(0, pos)}${emoji}${value.slice(pos)}`;
    applyNextValue(next, pos + emoji.length);
  };

  // Count the serialized string directly — the console's own counter under-counts list lines by
  // 2 characters; we are not reproducing that quirk.
  const charCount = value.length;

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-body">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-body-heading">Body*</Typography>
        </div>
        <Tooltip
          placement="left"
          title="The main text of your message. Use the toolbar to add basic formatting or a variable."
          data-testid="bloomreach-templates-form-body-tooltip"
        >
          <span>
            <Icon name="question" size={18} showCursor />
          </span>
        </Tooltip>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Enter the text for your message in the language you have selected.
      </Typography>

      {/* B5: console uses Alert type="info" for this banner (Body/index.tsx's own
          template_body_recommendations), not a custom div — same text, same data-testid. */}
      <Alert
        type="info"
        description="The text limit in the body should be less than 550 characters and emojis should be less than 10 to avoid any template rejection"
        data-testid="bloomreach-templates-form-body-banner"
      />

      <TextArea
        inputRef={textareaRef}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        minRows={4}
        multiline
        fullWidth
        error={Boolean(errors['body.value'])}
        helperText={friendlyError(errors['body.value'])}
        inputProps={{ maxLength: BODY_MAX_CHARS, 'data-testid': 'bloomreach-templates-form-body-textarea' }}
      />

      <div className="br-tpl-form-body-toolbar">
        <Button
          size="small"
          variant="outlined"
          startIcon="plus"
          disabled={disabled}
          onClick={handleAddVariable}
          data-testid="bloomreach-templates-form-body-add-variable"
        >
          Add variable
        </Button>
        <EmojiPickerButton
          onEmojiSelect={handleEmojiSelect}
          disabled={disabled}
          dataTestId="bloomreach-templates-form-body-toolbar-emoji"
          popoverTestId="bloomreach-templates-form-body-emoji-picker"
        />
        {TOOLBAR_ACTIONS.map((action) => (
          <Button
            key={action.key}
            size="small"
            variant="text"
            disabled={disabled}
            onClick={() => handleToolbarClick(action)}
            data-testid={`bloomreach-templates-form-body-toolbar-${action.key}`}
          >
            <Icon customPath={action.icon} size={18} alt={action.alt} />
          </Button>
        ))}
        <Typography variant="caption" className="br-tpl-form-char-count" data-testid="bloomreach-templates-form-body-count">
          {`Character count = ${charCount}`}
        </Typography>
      </div>

      {/* B4: sample-value inputs for the body's {{n}} tokens, moved in from the old standalone
          Variables card — console parity (Body/index.tsx:149-154 puts its own Variables here,
          after an <hr>, once there's at least one variable). */}
      {hasBodyVariables && (
        <>
          <hr />
          <BodySampleFields state={state} dispatch={dispatch} errors={errors} disabled={disabled} />
        </>
      )}
    </div>
  );
};

export default BodySection;
